import { useState } from 'react'
import { GraduationCap, Plus } from 'lucide-react'
import {
  EmptyState, ErrorState, FilterBar, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useTrainingNeedMutations, useTrainingNeeds } from '../api'
import {
  NEXT_STATUSES, TRAINING_PRIORITY_LABELS, TRAINING_SOURCE_LABELS, TRAINING_STATUS_LABELS,
} from '../types'
import type { TrainingPriority, TrainingStatus } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

interface Draft {
  employeeId: string
  competencyLabel: string
  competencyRef: string
  priority: TrainingPriority
  targetDate: string
  notes: string
}

const EMPTY: Draft = {
  employeeId: '',
  competencyLabel: '',
  competencyRef: '',
  priority: 'MEDIUM',
  targetDate: '',
  notes: '',
}

const STATUS_TONE: Record<TrainingStatus, 'neutral' | 'info' | 'success' | 'warning'> = {
  IDENTIFIED: 'warning',
  NOMINATED: 'info',
  SCHEDULED: 'info',
  COMPLETED: 'success',
  DROPPED: 'neutral',
}

/**
 * What people have been found to need, and whether anything came of it.
 *
 * <p>The list is the point rather than the creation form. A need that is identified and then
 * sits at IDENTIFIED for a year is the normal failure of this process, and it is only visible
 * if the open ones are what the screen shows first.
 */
export function TrainingNeedsPage() {
  useBreadcrumb()
  const [status, setStatus] = useState<TrainingStatus | ''>('')
  const [priority, setPriority] = useState<TrainingPriority | ''>('')
  const needs = useTrainingNeeds(status, priority)
  const { identify, move } = useTrainingNeedMutations()

  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onSave() {
    if (!draft) return
    setError(null)
    try {
      await identify.mutateAsync({
        employeeId: draft.employeeId,
        competencyLabel: draft.competencyLabel,
        competencyRef: draft.competencyRef || null,
        priority: draft.priority,
        targetDate: draft.targetDate || null,
        notes: draft.notes || null,
      })
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That need could not be recorded')
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Training needs"
        subtitle="Gaps somebody has been found to have, and what happened next."
        actions={
          <Button type="button" onClick={() => { setError(null); setDraft({ ...EMPTY }) }}>
            <Plus className="size-4" aria-hidden="true" />
            Identify a need
          </Button>
        }
      />

      <FilterBar>
        <Select
          aria-label="Status"
          id="need-status"
          value={status}
          onChange={(event) => setStatus(event.target.value as TrainingStatus | '')}
        >
          <option value="">Any status</option>
          {Object.entries(TRAINING_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </Select>

        <Select
          aria-label="Priority"
          id="need-priority"
          value={priority}
          onChange={(event) => setPriority(event.target.value as TrainingPriority | '')}
        >
          <option value="">Any priority</option>
          {Object.entries(TRAINING_PRIORITY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </Select>
      </FilterBar>

      <Card>
        {needs.isPending && <Skeleton className="m-4 h-24" />}
        {needs.isError && <ErrorState error={needs.error} onRetry={() => void needs.refetch()} />}
        {needs.isSuccess && needs.data.length === 0 && (
          <EmptyState
            title="No training needs recorded"
            description="Appraisals write these automatically from Phase 6. Until then they are raised by hand."
          />
        )}
        {needs.isSuccess && needs.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-start text-slate-600">
                <tr>
                  <th className="p-3 font-medium">Person</th>
                  <th className="p-3 font-medium">Competency</th>
                  <th className="p-3 font-medium">Source</th>
                  <th className="p-3 font-medium">Priority</th>
                  <th className="p-3 font-medium">Target</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium">Move to</th>
                </tr>
              </thead>
              <tbody>
                {needs.data.map((need) => (
                  <tr key={need.id} className="border-b border-slate-100">
                    <td className="p-3">
                      {need.employeeName ?? 'Unknown'}
                      {need.employeeCode && (
                        <span className="ms-1 text-slate-500">({need.employeeCode})</span>
                      )}
                    </td>
                    <td className="p-3">
                      {need.competencyLabel}
                      {need.notes && <span className="block text-slate-600">{need.notes}</span>}
                    </td>
                    <td className="p-3 text-slate-600">{TRAINING_SOURCE_LABELS[need.source]}</td>
                    <td className="p-3">{TRAINING_PRIORITY_LABELS[need.priority]}</td>
                    <td className="p-3 text-slate-600">{need.targetDate ?? '—'}</td>
                    <td className="p-3">
                      <StatusBadge
                        status={need.status}
                        tone={STATUS_TONE[need.status]}
                        label={TRAINING_STATUS_LABELS[need.status]}
                      />
                    </td>
                    <td className="p-3">
                      {NEXT_STATUSES[need.status].length === 0 ? (
                        <span className="text-slate-500">closed</span>
                      ) : (
                        <div className="flex gap-1">
                          {NEXT_STATUSES[need.status].map((next) => (
                            <Button
                              key={next}
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={move.isPending}
                              onClick={() => move.mutate({ id: need.id, status: next })}
                            >
                              {TRAINING_STATUS_LABELS[next]}
                            </Button>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <GraduationCap className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Appraisals will raise these automatically once the appraisal cycle exists in Phase 6.
        The receiving side is built and tested; nothing calls it yet.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title="Identify a training need"
        description="One open need per person per competency. Closing one lets the same gap recur later."
        submitLabel="Record it"
        loading={identify.isPending}
        error={error}
        onSubmit={() => void onSave()}
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="need-employee">Person</Label>
              <EmployeePicker
                  id="need-employee"
                  value={draft.employeeId}
                  onChange={(employeeId) => setDraft({ ...draft, employeeId: employeeId ?? '' })}
                />
            </div>
            <div>
              <Label htmlFor="need-label">Competency</Label>
              <Input
                id="need-label"
                value={draft.competencyLabel}
                placeholder="Forklift operation, IFRS 16, spoken Arabic…"
                onChange={(event) => setDraft({ ...draft, competencyLabel: event.target.value })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="need-priority-input">Priority</Label>
                <Select
                  id="need-priority-input"
                  className="w-full"
                  value={draft.priority}
                  onChange={(event) =>
                    setDraft({ ...draft, priority: event.target.value as TrainingPriority })}
                >
                  {Object.entries(TRAINING_PRIORITY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="need-target">Target date</Label>
                <DatePicker
                  id="need-target"
                  value={draft.targetDate}
                  onChange={(event) => setDraft({ ...draft, targetDate: event.target.value })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="need-notes">Notes</Label>
              <Input
                id="need-notes"
                value={draft.notes}
                onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
